import { ApiResponse, ApiValidationError } from './types';
import { API_BASE_URL } from '../config/runtimeConfig';

const DEFAULT_ERROR_MESSAGE = 'Не удалось выполнить действие. Попробуйте ещё раз.';
const NETWORK_ERROR_MESSAGE = 'Нет связи с сервисом. Проверьте подключение и попробуйте снова.';

let accessToken: string | null = null;

export class ApiClientError extends Error {
  public readonly errors: Record<string, string[]>;
  public readonly statusCode: number | undefined;

  public constructor(apiError: ApiValidationError, statusCode?: number) {
    super(normalizeApiMessage(apiError.message, statusCode));

    this.name = 'ApiClientError';
    this.errors = apiError.errors ?? {};
    this.statusCode = statusCode;
  }
}

export function setHttpToken(token: string | null): void {
  accessToken = token;
}

export async function httpGet<TData>(path: string, queryParams?: Record<string, string>): Promise<TData> {
  const url = createUrl(path, queryParams);

  return request<TData>(url, {
    method: 'GET',
  });
}

export async function httpPost<TData, TPayload = unknown>(path: string, payload?: TPayload, options?: { idempotencyKey: string }): Promise<TData> {
  const url = createUrl(path);
  const body = payload === undefined ? undefined : JSON.stringify(payload);

  return request<TData>(url, {
    body,
    method: 'POST',
  }, true, options?.idempotencyKey);
}

export async function httpPostResponse<TData>(path: string, payload: unknown, options?: { idempotencyKey: string }): Promise<ApiResponse<TData>> {
  const url = createUrl(path);
  const body = JSON.stringify(payload);
  return request<ApiResponse<TData>>(url, { body, method: 'POST' }, true, options?.idempotencyKey, false);
}

export async function httpPostForm<TData>(path: string, formData: FormData): Promise<TData> {
  const url = createUrl(path);

  return request<TData>(url, {
    body: formData,
    method: 'POST',
  }, false);
}

export async function httpPut<TData, TPayload = unknown>(path: string, payload: TPayload): Promise<TData> {
  const url = createUrl(path);
  const body = JSON.stringify(payload);

  return request<TData>(url, {
    body,
    method: 'PUT',
  });
}

export async function httpDeleteResponse<TData>(path: string, options: { idempotencyKey: string }): Promise<ApiResponse<TData>> {
  const url = createUrl(path);
  return request<ApiResponse<TData>>(url, { method: 'DELETE' }, false, options.idempotencyKey, false);
}

export async function httpDelete(path: string): Promise<void> {
  const url = createUrl(path);

  await request<void>(url, {
    method: 'DELETE',
  });
}

async function request<TData>(url: string, init: RequestInit, withJsonContentType = true, idempotencyKey?: string, unwrap = true): Promise<TData> {
  const response = await safeFetch(url, {
    ...init,
    headers: createHeaders(withJsonContentType, idempotencyKey),
  });
  const responseBody = await readJson(response);

  if (!response.ok) {
    const apiError = responseBody as ApiValidationError;

    const retryAfter = response.status === 429 ? response.headers?.get('Retry-After') : null;
    const retrySeconds = retryAfter === null || retryAfter === undefined ? NaN : Number(retryAfter);

    if (Number.isFinite(retrySeconds) && retrySeconds > 0) {
      const waitSeconds = Math.ceil(retrySeconds);
      const rateLimitError = { ...apiError, message: `Слишком много запросов. Повторите попытку через ${waitSeconds} с.` };
      throw new ApiClientError(rateLimitError, response.status);
    }

    throw new ApiClientError(apiError, response.status);
  }

  if (unwrap && isApiResponse<TData>(responseBody)) {
    return responseBody.data;
  }

  return responseBody as TData;
}

export async function httpGetResponse<TData>(path: string, queryParams?: Record<string, string>, signal?: AbortSignal, cache?: RequestCache): Promise<ApiResponse<TData>> {
  const url = createUrl(path, queryParams);
  const response = await safeFetch(url, {
    headers: createHeaders(),
    method: 'GET',
    signal,
    cache,
  });
  const responseBody = await readJson(response);

  if (!response.ok) {
    const apiError = responseBody as ApiValidationError;

    throw new ApiClientError(apiError, response.status);
  }

  return responseBody as ApiResponse<TData>;
}

export async function httpDownload(
  path: string,
  queryParams?: Record<string, string>,
  fallbackFileName?: string,
): Promise<void> {
  const url = createUrl(path, queryParams);
  const response = await safeFetch(url, {
    headers: createHeaders(false),
    method: 'GET',
  });

  if (!response.ok) {
    const responseBody = await readJson(response);
    const apiError = responseBody as ApiValidationError;

    throw new ApiClientError(apiError, response.status);
  }

  const fileBlob = await response.blob();
  const objectUrl = URL.createObjectURL(fileBlob);
  const link = document.createElement('a');
  const contentDisposition = response.headers.get('Content-Disposition');
  const fileName = resolveFileName(contentDisposition) ?? fallbackFileName ?? 'download';

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

function createUrl(path: string, queryParams?: Record<string, string>): string {
  const url = new URL(path, API_BASE_URL);

  if (queryParams === undefined) {
    return url.toString();
  }

  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  return url.toString();
}

async function safeFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    if (init.signal?.aborted || (error instanceof Error && error.name === 'AbortError')) throw error;
    throw new Error(NETWORK_ERROR_MESSAGE);
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new Error(DEFAULT_ERROR_MESSAGE);
  }
}

function normalizeApiMessage(message: unknown, statusCode?: number): string {
  const normalizedMessage = typeof message === 'string' ? message.trim() : '';
  const containsTechnicalDetails = /internal server error|failed to fetch|networkerror|load failed|sqlstate|stack trace|unexpected token|syntaxerror|exception|cannot read propert/i.test(normalizedMessage);

  if (statusCode !== undefined && statusCode >= 500) {
    return DEFAULT_ERROR_MESSAGE;
  }

  if (normalizedMessage === '' || containsTechnicalDetails) {
    return DEFAULT_ERROR_MESSAGE;
  }

  return normalizedMessage;
}

function createHeaders(withJsonContentType = true, idempotencyKey?: string): HeadersInit {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (withJsonContentType) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken !== null) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (idempotencyKey !== undefined) headers['Idempotency-Key'] = idempotencyKey;

  return headers;
}

function isApiResponse<TData>(value: unknown): value is ApiResponse<TData> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'success' in value && 'data' in value;
}

function resolveFileName(contentDisposition: string | null): string | null {
  if (contentDisposition === null) {
    return null;
  }

  const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utfMatch !== null) {
    return decodeURIComponent(utfMatch[1]);
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  if (plainMatch !== null) {
    return plainMatch[1];
  }

  return null;
}
